// ============================================================================
// Facebook Page Webhook Adapter
//
// Verifies Meta's `x-hub-signature-256` HMAC and parses Page `feed` changes
// into provider-neutral social-posting webhook-change events. This is scoped to
// moderation/cache invalidation; full content sync remains owned by callers
// through the comment/list APIs.
// ============================================================================

import type {
  SocialPostingWebhookChangeEvent,
  SocialPostingWebhookChangeKind,
} from '../../capabilities/social-posting'
import {
  hmac,
  hexToBytes,
  parsePrefixedSignature,
  timingSafeEqualBytes,
} from '../../services/webhook-verifier'
import type {
  ParsedWebhookEvent,
  WebhookAdapter,
  WebhookAdapterContext,
} from '../../services/webhook-adapters'

interface FacebookPageWebhookPayload {
  object?: string
  entry?: ReadonlyArray<FacebookPageWebhookEntry>
}

interface FacebookPageWebhookEntry {
  id?: string
  time?: number
  changes?: ReadonlyArray<FacebookPageWebhookChange>
}

interface FacebookPageWebhookChange {
  field?: string
  value?: Record<string, unknown>
}

async function verifyWebhook(ctx: WebhookAdapterContext): Promise<boolean> {
  const appSecret = pickSecret(ctx.env)
  if (!appSecret) return false

  const header = ctx.headers.get('x-hub-signature-256')
  if (!header) return false

  const candidateHex = parsePrefixedSignature(header, 'sha256')
  if (!candidateHex) return false

  let candidateBytes: Uint8Array
  try {
    candidateBytes = hexToBytes(candidateHex.trim())
  } catch {
    return false
  }

  let expectedBytes: Uint8Array
  try {
    expectedBytes = await hmac(appSecret, ctx.rawBody, 'SHA-256')
  } catch {
    return false
  }

  return timingSafeEqualBytes(candidateBytes, expectedBytes)
}

async function parseWebhook(ctx: WebhookAdapterContext): Promise<ParsedWebhookEvent[]> {
  const text = new TextDecoder().decode(new Uint8Array(ctx.rawBody))
  const payload = JSON.parse(text) as FacebookPageWebhookPayload

  const entries = Array.isArray(payload.entry) ? payload.entry : []
  const events: ParsedWebhookEvent[] = []

  for (const entry of entries) {
    const pageId = typeof entry.id === 'string' ? entry.id : ''
    const changes = Array.isArray(entry.changes) ? entry.changes : []

    for (const change of changes) {
      if (change.field !== 'feed') continue
      const value = change.value && typeof change.value === 'object' ? change.value : {}
      const item = readString(value.item)
      const verb = readString(value.verb)
      const providerPostId = readString(value.post_id) || readString(value.postId)
      const providerCommentId = readString(value.comment_id) || readString(value.commentId)
      const at = isoFromTimestamp(readNumber(value.created_time) ?? entry.time)

      const eventId = [
        'facebook-page',
        pageId,
        change.field,
        verb || 'unknown',
        item || 'unknown',
        providerPostId || 'no-post',
        providerCommentId || 'no-comment',
        readNumber(value.created_time) ?? entry.time ?? 'no-time',
      ].join(':')

      const changeKind = toChangeKind(item, verb)
      const webhookPayload: SocialPostingWebhookChangeEvent = {
        type: 'social_posting.webhook_change',
        capability: 'social-posting',
        instanceId: '',
        companyId: '',
        targetAccountId: pageId,
        changeKind,
        at,
        metadata: {
          field: change.field,
          item,
          verb,
        },
      }
      if (providerPostId) webhookPayload.providerPostId = providerPostId
      if (providerCommentId) webhookPayload.providerCommentId = providerCommentId
      const actorId = readString(value.sender_id) || readNestedString(value.from, 'id')
      const actorName = readString(value.sender_name) || readNestedString(value.from, 'name')
      const message = readString(value.message)
      if (actorId) webhookPayload.actorId = actorId
      if (actorName) webhookPayload.actorName = actorName
      if (message) webhookPayload.message = message

      events.push({
        eventId,
        eventType: 'social_posting.webhook_change',
        capability: 'social-posting',
        payload: webhookPayload,
      })
    }
  }

  return events
}

function pickSecret(env: Record<string, unknown>): string | null {
  const specific = env?.FACEBOOK_PAGE_APP_SECRET
  if (typeof specific === 'string' && specific.length > 0) return specific
  const fallback = env?.FB_APP_SECRET
  if (typeof fallback === 'string' && fallback.length > 0) return fallback
  return null
}

function toChangeKind(item: string, verb: string): SocialPostingWebhookChangeKind {
  const normalisedVerb = verb.toLowerCase()
  if (item === 'post') {
    if (normalisedVerb === 'add') return 'post.created'
    if (normalisedVerb === 'edited' || normalisedVerb === 'edit') return 'post.updated'
    if (normalisedVerb === 'remove' || normalisedVerb === 'delete') return 'post.deleted'
  }
  if (item === 'comment') {
    if (normalisedVerb === 'add') return 'comment.created'
    if (normalisedVerb === 'edited' || normalisedVerb === 'edit') return 'comment.updated'
    if (normalisedVerb === 'remove' || normalisedVerb === 'delete') return 'comment.deleted'
  }
  return 'unknown'
}

function readString(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function readNestedString(value: unknown, key: string): string {
  if (!value || typeof value !== 'object') return ''
  return readString((value as Record<string, unknown>)[key])
}

function readNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

function isoFromTimestamp(secondsOrMs: number | undefined): string {
  if (typeof secondsOrMs !== 'number' || !Number.isFinite(secondsOrMs) || secondsOrMs <= 0) {
    return new Date(0).toISOString()
  }
  const ms = secondsOrMs < 10_000_000_000 ? secondsOrMs * 1000 : secondsOrMs
  return new Date(ms).toISOString()
}

export const facebookPageWebhookAdapter: WebhookAdapter = {
  verifyWebhook,
  parseWebhook,
}
